import React, { useRef, useState } from "react"
import { createPortal } from "react-dom"
import { Button, Text } from "atomize"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPen, faTrashAlt, faXmark } from "@fortawesome/free-solid-svg-icons"
import { theme } from "../../settings/config"
import SchemaDrawing from "./SchemaDrawing"

// I pezzi dello schema che stanno fuori dall'editor: il tasto col campo
// accanto a un esercizio, la miniatura sotto, e lo schema grande che si
// apre toccandola nella lezione.

// Il campo in prospettiva, a tratto, con la rete: l'icona del tasto che
// apre l'editor. Dall'alto, a quella misura, sembrava un palazzo.
export const CourtIcon = () => (
	<svg
		viewBox="0 0 24 24"
		width="1.125em"
		height="1.125em"
		aria-hidden="true"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round">
		<path d="M7 5h10l4 15H3z" />
		<path d="M4.6 13.5h14.8M12 8v9.5" />
		<path d="M4.6 10.5v3M19.4 10.5v3" />
	</svg>
)

// La miniatura: lo schema in prospettiva e basta, senza un riquadro
// intorno. Il campo ha già la sua forma, con gli angoli tondi e la cornice
// verde; un fondo in più gli faceva solo da contorno.
export const SchemaThumbnail = ({ schema, width, height }) => (
	<span style={{ display: "block", flex: "none", width, height }}>
		<SchemaDrawing schema={schema} />
	</span>
)

// Dove sta una miniatura, per l'editor che si apre crescendo da lei
const originOf = ref => () => {
	const rect = ref.current?.getBoundingClientRect()
	return { court: rect, area: rect, radius: 12 }
}

// Sotto un esercizio nel foglio del programma: la miniatura, che come
// "Modifica" riapre l'editor, e la ✕ per toglierlo. `onEdit` riceve dove
// sta la miniatura. La miniatura cade sul
// bordo del testo dell'esercizio (dopo il pallino e il suo spazio), la ✕
// sotto quella dell'esercizio.
export function ExerciseSchemaRow({ schema, name, disabled, onEdit, onRemove }) {
	const thumbnail = useRef(null)
	const edit = () => onEdit(originOf(thumbnail))
	return (
		<div style={{ display: "flex", alignItems: "center", gap: "0.625rem", marginLeft: "1.125rem" }}>
			<button
				type="button"
				ref={thumbnail}
				aria-label={`Modifica lo schema: ${name}`}
				disabled={disabled}
				onClick={edit}
				style={{ border: 0, padding: 0, background: "none", cursor: "pointer" }}>
				<SchemaThumbnail schema={schema} width="4rem" height="4.5rem" />
			</button>
			<div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
				<Text textSize="caption" textColor={theme.colors.text_light}>
					Schema
				</Text>
				<Button
					h="auto"
					p="0"
					bg="transparent"
					textColor={theme.colors.primary}
					textWeight="600"
					textSize="caption"
					disabled={disabled}
					disableBg="transparent"
					onClick={edit}>
					Modifica
				</Button>
			</div>
			<Button
				h="2.25rem"
				w="2.25rem"
				p="0"
				bg="transparent"
				aria-label={`Togli lo schema: ${name}`}
				disabled={disabled}
				disableBg="transparent"
				onClick={onRemove}>
				<FontAwesomeIcon icon={faXmark} color={theme.colors.text_light} />
			</Button>
		</div>
	)
}

// Dove lo schema è la cosa da guardare (una voce del quaderno) si vede
// grande: largo quanto c'è, fino a una misura che sul telefono riempie lo
// sguardo senza spingere via tutto il resto
const LARGE = { width: "min(100%, 16rem)", aspectRatio: "358 / 400" }

// Lo schema grande, nel foglio di una voce del quaderno: lì l'esercizio è
// uno solo, e il suo disegno è la cosa più importante della pagina. Si tocca
// per cambiarlo; sotto, a destra, le pastiglie delle azioni come nella
// lezione aperta (LessonActions): il cestino tondo e "Modifica schema".
export function SchemaCard({ schema, disabled, onEdit, onRemove }) {
	const drawing = useRef(null)
	const edit = () => onEdit(originOf(drawing))
	return (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				gap: "0.5rem",
				padding: "0.75rem",
				borderRadius: "15px",
				background: theme.colors.surface,
			}}>
			<button
				type="button"
				ref={drawing}
				aria-label="Modifica lo schema"
				disabled={disabled}
				onClick={edit}
				style={{ border: 0, padding: 0, background: "none", cursor: "pointer", display: "block", ...LARGE }}>
				<SchemaThumbnail schema={schema} width="100%" height="100%" />
			</button>
			<div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem", alignSelf: "stretch" }}>
				<Button
					h="2.5rem"
					w="2.5rem"
					p="0"
					bg={theme.colors.gunmetal_fill}
					rounded={theme.rounded["20px"]}
					aria-label="Togli lo schema"
					disabled={disabled}
					onClick={onRemove}>
					<FontAwesomeIcon icon={faTrashAlt} color={theme.colors.white} />
				</Button>
				<Button
					h="2.5rem"
					p={{ x: "1rem" }}
					bg={theme.colors.gunmetal_fill}
					rounded={theme.rounded["20px"]}
					style={{ gap: "0.5rem" }}
					disabled={disabled}
					onClick={edit}>
					<FontAwesomeIcon icon={faPen} color={theme.colors.white} />
					<Text tag="span" textWeight="bold" textColor={theme.colors.white}>
						Modifica schema
					</Text>
				</Button>
			</div>
		</div>
	)
}

// Nella lezione aperta: la miniatura accanto all'esercizio; `large` nel
// quaderno, dove lo schema è la cosa da guardare. Toccato, lo schema si
// apre grande sopra a tutto, e si chiude toccandolo ancora.
export function SchemaPreview({ schema, label, large = false }) {
	const [isOpen, setOpen] = useState(false)
	return (
		<>
			<button
				type="button"
				aria-label={`Apri lo schema: ${label}`}
				onClick={event => {
					// sta dentro il riquadro del programma, che toccato apre il foglio
					event.stopPropagation()
					setOpen(true)
				}}
				style={{
					border: 0,
					padding: 0,
					background: "none",
					cursor: "zoom-in",
					flex: "none",
					...(large ? { display: "block", ...LARGE } : {}),
				}}>
				{large ? (
					<SchemaThumbnail schema={schema} width="100%" height="100%" />
				) : (
					<SchemaThumbnail schema={schema} width="5.75rem" height="6.25rem" />
				)}
			</button>
			{isOpen &&
				createPortal(
					<div
						role="dialog"
						aria-modal="true"
						aria-label={`Schema: ${label}`}
						// in React un tocco risale anche da un portale: fin qui e basta
						onClick={event => {
							event.stopPropagation()
							setOpen(false)
						}}
						style={{
							position: "fixed",
							inset: 0,
							zIndex: 1200,
							background: theme.colors.background,
							display: "flex",
							flexDirection: "column",
							padding:
								"calc(env(safe-area-inset-top) + 0.75rem) 1rem calc(env(safe-area-inset-bottom) + 1rem)",
							gap: "0.75rem",
							cursor: "zoom-out",
						}}>
						<div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
							<Text textSize="caption" textColor={theme.colors.text} style={{ flex: 1, minWidth: 0 }}>
								{label}
							</Text>
							<button
								type="button"
								aria-label="Chiudi lo schema"
								style={{
									flex: "none",
									width: "2.25rem",
									height: "2.25rem",
									border: 0,
									borderRadius: "50%",
									background: theme.colors.surface,
									color: theme.colors.text_light,
									cursor: "pointer",
								}}>
								<FontAwesomeIcon icon={faXmark} />
							</button>
						</div>
						<div style={{ flex: 1, minHeight: 0 }}>
							<SchemaDrawing schema={schema} label={`Schema: ${label}`} />
						</div>
					</div>,
					document.body,
				)}
		</>
	)
}
